import { useQueryClient } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// ===========================================================================
// BUSCAR EL ABONO QUE PAGA ESTE VOUCHER, sin salir del expediente.
//
// Pedido así: «también tiene que haber un botón de buscar alguna transacción o
// movimiento bancario que se le aparezca, para ya poder hacer también la
// conciliación del detalle». Y tiene sentido: quien está mirando el voucher ya
// sabe de qué pago habla, y hasta ahora tenía que irse a la mesa, acordarse del
// importe y buscarlo a mano.
//
// Arranca con lo que el voucher dice —su importe y su moneda— porque es lo que
// más recorta, y se puede afinar. NO decide: enseña candidatos y quien mira
// firma, igual que en la revisión guiada. La compuerta es la misma para todos.
// ===========================================================================

type Fila = {
  id: string;
  fecha: string;
  banco: string;
  moneda: "PEN" | "USD";
  importe: number;
  descripcion: string | null;
  operacion: string | null;
  estado: string;
};

const dinero = (c: number, m: string) =>
  `${m === "USD" ? "US$" : "S/"} ${(c / 100).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
  })}`;

export function BuscarMovimiento({
  voucher,
  comprobanteId,
  totalBoleta,
  alCerrar,
}: {
  voucher: {
    importe: number | null;
    moneda: string | null;
    operacion: string | null;
    fechaPago: string | null;
  };
  /** Contra qué boleta se cruzaría. Sin ella no se puede conciliar. */
  comprobanteId: string | null;
  /** Lo que vale la boleta: el respaldo cuando el voucher no trae importe. */
  totalBoleta: number | null;
  alCerrar: () => void;
}) {
  // ARRANCA POR EL IMPORTE Y CON EL TEXTO VACÍO.
  //
  // Tentaba precargar el número de operación del voucher, y probándolo se vio
  // que es peor: el banco rara vez lo escribe en la glosa, así que la búsqueda
  // abría con cero resultados sobre un pago que SÍ estaba —y el primer
  // resultado de una herramienta nueva es el que decide si se vuelve a usar.
  // El importe, en cambio, lo encontró a la primera.
  //
  // Y si el voucher no trae importe —el óptico falla justo en la cifra— se usa
  // el de la boleta: es lo que se cobró, y suele ser lo que entró.
  const [texto, setTexto] = useState("");
  const arranque = voucher.importe ?? totalBoleta;
  const [monto, setMonto] = useState(
    arranque === null ? "" : String(Math.round(arranque / 100)),
  );
  const [aplicado, setAplicado] = useState(0);
  const qc = useQueryClient();
  const decidir = api.decidir.useMutation();

  const centimos = Number.parseInt(monto.replace(/[^\d]/g, ""), 10);
  const hayMonto = Number.isFinite(centimos) && centimos > 0;

  const { data, isPending } = api.mesa.useQuery(
    ["buscar-mov", texto, monto, String(aplicado)],
    {
      query: {
        estado: "todos",
        buscar: texto || undefined,
        porPagina: 20,
        // Una unidad de margen a cada lado: el banco redondea distinto que el
        // voucher, y buscar el importe exacto esconde justo la línea buena.
        ...(hayMonto
          ? {
              importeMin: centimos * 100 - 99,
              importeMax: centimos * 100 + 99,
            }
          : {}),
        ...(voucher.moneda === "PEN" || voucher.moneda === "USD"
          ? { moneda: voucher.moneda }
          : {}),
      },
    },
  );
  const filas = (data?.status === 200 ? (data.body.filas ?? []) : []) as Fila[];

  const conciliar = (movimientoId: string) => {
    if (!comprobanteId) return;
    decidir.mutate(
      {
        body: { movimientoId, comprobanteId, respuesta: "si" },
      },
      {
        onSuccess: (r: { status: number; body: { dice?: string } }) => {
          if (r.status !== 200) {
            toast.error("No se pudo conciliar");
            return;
          }
          toast.success(r.body.dice ?? "Conciliado y firmado");
          qc.invalidateQueries();
          alCerrar();
        },
        onError: () => toast.error("No se pudo conciliar"),
      },
    );
  };

  return (
    <div className="mt-2 rounded-lg border border-cyan-600/40 bg-cyan-500/5">
      <div className="flex flex-wrap items-center gap-2 border-cyan-600/30 border-b px-3 py-2">
        <Search className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="font-semibold text-[12px]">
          Buscar el abono en el banco
        </span>
        <button
          type="button"
          onClick={alCerrar}
          className="ml-auto text-muted-foreground hover:text-foreground"
          aria-label="Cerrar la búsqueda"
        >
          <X className="size-3.5" />
        </button>
      </div>

      <form
        className="flex flex-wrap items-center gap-2 px-3 py-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAplicado((n) => n + 1);
        }}
      >
        <input
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          inputMode="numeric"
          aria-label="Importe del abono"
          placeholder="monto"
          className="w-[90px] rounded border bg-background px-2 py-1 text-right text-[12px] tabular-nums focus:border-cyan-500 focus:outline-none"
        />
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          aria-label="Buscar en la glosa o el número de operación"
          placeholder="glosa o n.º de operación"
          className="min-w-[180px] flex-1 rounded border bg-background px-2 py-1 text-[12px] focus:border-cyan-500 focus:outline-none"
        />
        <Button size="sm" variant="outline" type="submit">
          Buscar
        </Button>
      </form>

      <div className="max-h-[280px] overflow-auto border-cyan-600/30 border-t">
        {isPending ? (
          <p className="px-3 py-3 text-[12px] text-muted-foreground">
            Buscando…
          </p>
        ) : filas.length === 0 ? (
          <p className="px-3 py-3 text-[12px] text-muted-foreground">
            Ningún abono cuadra con eso. Probá quitando el número de operación:
            el banco no siempre lo escribe.
          </p>
        ) : (
          <ul className="divide-y">
            {filas.map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-1.5 text-[12px]"
              >
                <span className="w-[86px] shrink-0 tabular-nums">
                  {m.fecha}
                </span>
                <b className="w-[110px] shrink-0 text-right tabular-nums">
                  {dinero(m.importe, m.moneda)}
                </b>
                <span
                  className="min-w-0 flex-1 truncate text-muted-foreground"
                  title={m.descripcion ?? ""}
                >
                  {m.banco} · {m.descripcion ?? "—"}
                </span>
                {m.estado === "conciliado" ? (
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    ya conciliado
                  </span>
                ) : comprobanteId ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decidir.isPending}
                    onClick={() => conciliar(m.id)}
                  >
                    Es este
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p
        className={cn(
          "border-cyan-600/30 border-t px-3 py-1.5 text-[11px]",
          comprobanteId ? "text-muted-foreground" : "text-amber-700",
        )}
      >
        {comprobanteId
          ? "Al pulsar «Es este» queda conciliado a tu nombre y en la bitácora."
          : "Este expediente no tiene boleta registrada: no hay contra qué conciliar."}
      </p>
    </div>
  );
}
